// Сводная «Модели ГПР» — восьмой экран нового фронта (п.20, 2026-09-07).
// Строки: этап → работа → корпус (работа раскрывается кликом), колонки — периоды
// окончания в выбранном масштабе. Показатель: этажи (по дате окончания) или объём,
// равномерно разложенный по дням между началом и окончанием задачи.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { fmt } from '../util';

type PivotRow = Data<'/api/projects/{pid}/pivot', 'get'>[number];
const D1 = 86400000;
const dnum = (s?: string | null) => (s ? Math.round(new Date(s + 'T00:00:00Z').getTime() / D1) : 0);
const MN = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function period(d: string, scale: string): string {
  if (scale === 'day') return d;
  if (scale === 'week') {
    const dt = new Date(d + 'T00:00Z');
    dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7));
    return dt.toISOString().slice(0, 10);
  }
  if (scale === 'quarter') return d.slice(0, 4) + '-Q' + (Math.floor((+d.slice(5, 7) - 1) / 3) + 1);
  return d.slice(0, 7);
}
type Agg = { byM: Map<string, number>; total: number; minN: string | null; maxD: string | null };
const emptyAgg = (): Agg => ({ byM: new Map(), total: 0, minN: null, maxD: null });
function sumInto(dst: Agg, a: Agg) {
  a.byM.forEach((v, m) => dst.byM.set(m, (dst.byM.get(m) || 0) + v));
  dst.total += a.total;
  if (a.minN && (!dst.minN || a.minN < dst.minN)) dst.minN = a.minN;
  if (a.maxD && (!dst.maxD || a.maxD > dst.maxD)) dst.maxD = a.maxD;
}

export function Pivot({ ctx }: { ctx: ShellCtx }) {
  const [data, setData] = useState<PivotRow[] | null>(null);
  const [scale, setScale] = useState(() => { try { return localStorage.getItem('plot_pvscale') || 'month'; } catch { return 'month'; } });
  const [metric, setMetric] = useState(() => { try { return localStorage.getItem('plot_pvmetric') || 'cnt'; } catch { return 'cnt'; } });
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [openWork, setOpenWork] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    const r = await call<PivotRow[]>('get', p('/api/projects/{pid}/pivot', { pid: ctx.pid }));
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    setData(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);
  const save = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* приватный режим */ } };

  const view = useMemo(() => {
    const rows = (data || []).filter(x => (!ctx.matchesWork || ctx.matchesWork(x.r))
      && (!ctx.matchesKorpus || ctx.matchesKorpus(x.k)) && (!ctx.podr || x.p === ctx.podr));
    const agg = new Map<string, { vals: [string, string, string] } & Agg>();
    rows.forEach(x => {
      const vals: [string, string, string] = [x.e, x.r, x.k];
      const key = vals.join('¶');
      let a = agg.get(key);
      if (!a) { a = { vals, ...emptyAgg() }; agg.set(key, a); }
      if (metric === 'obm') {
        const v = +(x.v || 0);
        if (v > 0 && x.n && x.d) {
          const t0 = dnum(x.n), t1 = Math.max(dnum(x.d), dnum(x.n));
          const share = v / (t1 - t0 + 1);
          for (let t = t0; t <= t1; t++) {
            const m = period(new Date(t * D1).toISOString().slice(0, 10), scale);
            a.byM.set(m, (a.byM.get(m) || 0) + share);
          }
          a.total += v;
        }
      } else if (x.d) {
        const m = period(x.d, scale);
        a.byM.set(m, (a.byM.get(m) || 0) + 1);
        a.total++;
      }
      if (x.n && (!a.minN || x.n < a.minN)) a.minN = x.n;
      if (x.d && (!a.maxD || x.d > a.maxD)) a.maxD = x.d;
    });
    const months = [...new Set(([] as string[]).concat(...[...agg.values()].map(a => [...a.byM.keys()])))].sort();
    const list = [...agg.values()].sort((a, b) => {
      for (let i = 0; i < 3; i++) {
        const x = String(a.vals[i]).toLowerCase(), y = String(b.vals[i]).toLowerCase();
        if (x !== y) return x < y ? -1 : 1;
      }
      return 0;
    });
    const tree = new Map<string, Agg & { works: Map<string, Agg & { korp: (Agg & { vals: [string, string, string] })[] }> }>();
    list.forEach(a => {
      const [e0, r0] = a.vals;
      if (!tree.has(e0)) tree.set(e0, { ...emptyAgg(), works: new Map() });
      const E = tree.get(e0)!;
      sumInto(E, a);
      if (!E.works.has(r0)) E.works.set(r0, { ...emptyAgg(), korp: [] });
      const R = E.works.get(r0)!;
      sumInto(R, a);
      R.korp.push(a);
    });
    const tot = emptyAgg();
    list.forEach(a => sumInto(tot, a));
    return { months, tree, tot };
  }, [data, scale, metric, ctx]);

  const mLabel = (m: string) => (scale === 'day' ? fmt(m) : scale === 'week' ? 'нед ' + fmt(m)
    : scale === 'quarter' ? m.slice(5) + ' ' + m.slice(2, 4) : MN[+m.slice(5) - 1] + ' ' + m.slice(2, 4));
  const fv = (v?: number) => (metric === 'obm' ? (Math.round(v || 0) || '') : (v || ''));
  const cells = (a: Agg) => view.months.map(m => <td key={m} className="num">{fv(a.byM.get(m))}</td>);
  const dcells = (a: Agg, bg: string) => <>
    <td className="pv-fix" style={{ left: 510, background: bg }}>{a.minN ? fmt(a.minN) : ''}</td>
    <td className="pv-fix" style={{ left: 588, background: bg }}>{a.maxD ? fmt(a.maxD) : ''}</td></>;

  return (
    <div>
      <div className="bar">
        <label>Показатель <select value={metric} onChange={e => { setMetric(e.target.value); save('plot_pvmetric', e.target.value); }}>
          <option value="cnt">Этажи</option><option value="obm">Объём</option></select></label>
        <label>Масштаб <select value={scale} onChange={e => { setScale(e.target.value); save('plot_pvscale', e.target.value); }}>
          <option value="day">День</option><option value="week">Неделя</option>
          <option value="month">Месяц</option><option value="quarter">Квартал</option></select></label>
        <span className="pw-muted">Строки: этап работ → работа.</span>
        <span className="grow" />
        <button className="sec" onClick={() => void load()}>Обновить данные</button>
      </div>
      <div className="tbl" style={{ maxHeight: '74vh' }}>
        <table id="pivotTable">
          <thead><tr>
            {['Этап работ', 'Работа', 'Корпус'].map((t, i) => (
              <th key={t} className="pv-fix" style={{ left: i * 170, minWidth: 170, maxWidth: 170 }}>{t}</th>))}
            <th className="pv-fix" style={{ left: 510, minWidth: 78 }} title="Минимальное начало по группе">Начало</th>
            <th className="pv-fix" style={{ left: 588, minWidth: 78 }} title="Максимальное окончание по группе">Окончание</th>
            {view.months.map(m => <th key={m} className="num">{mLabel(m)}</th>)}
            <th className="num">Итого</th>
          </tr></thead>
          <tbody>
            {[...view.tree.entries()].map(([e0, E]) => {
              const coll = collapsed.has(e0);
              return (
                <React.Fragment key={e0}>
                  <tr style={{ fontWeight: 600, cursor: 'pointer', background: '#f1f4f8' }}
                    onClick={() => setCollapsed(s => { const n = new Set(s); if (n.has(e0)) n.delete(e0); else n.add(e0); return n; })}>
                    <td colSpan={3} className="pv-fix" style={{ left: 0, background: '#f1f4f8' }}>{(coll ? '▸ ' : '▾ ') + e0}</td>
                    {dcells(E, '#f1f4f8')}{cells(E)}<td className="num">{fv(E.total)}</td>
                  </tr>
                  {!coll && [...E.works.entries()].map(([r0, R]) => {
                    const wkey = e0 + '¶' + r0;
                    const wopen = openWork.has(wkey);
                    return (
                      <React.Fragment key={wkey}>
                        <tr>
                          <td className="pv-fix" style={{ left: 0, background: '#fff' }}></td>
                          <td className="pv-fix" style={{ left: 170, background: '#fff', cursor: 'pointer' }}
                            title="клик — развернуть по корпусам"
                            onClick={() => setOpenWork(s => { const n = new Set(s); if (n.has(wkey)) n.delete(wkey); else n.add(wkey); return n; })}>
                            {(wopen ? '▾ ' : '▸ ') + r0}</td>
                          <td className="pv-fix" style={{ left: 340, background: '#fff' }}></td>
                          {dcells(R, '#fff')}{cells(R)}<td className="num">{fv(R.total)}</td>
                        </tr>
                        {wopen && R.korp.map(a => (
                          <tr key={a.vals.join('¶')}>
                            <td className="pv-fix" style={{ left: 0, background: '#fff' }}></td>
                            <td className="pv-fix" style={{ left: 170, background: '#fff' }}></td>
                            <td className="pv-fix" style={{ left: 340, background: '#fff' }}>{a.vals[2]}</td>
                            {dcells(a, '#fff')}{cells(a)}<td className="num">{fv(a.total)}</td>
                          </tr>))}
                      </React.Fragment>);
                  })}
                </React.Fragment>);
            })}
            <tr style={{ fontWeight: 700, background: '#eef1f5' }}>
              <td colSpan={3} className="pv-fix" style={{ left: 0, background: '#eef1f5' }}>Итого</td>
              {dcells(view.tot, '#eef1f5')}{cells(view.tot)}<td className="num">{fv(view.tot.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="pw-muted" style={{ marginTop: 8 }}>Колонки — период окончания (масштаб выбирается), в ячейках —
        количество задач. Клик по строке этапа сворачивает его работы; клик по работе разворачивает её по корпусам.
        Действуют все фильтры сверху.</div>
    </div>
  );
}
